import { useEffect } from "react";

const SHADOW_SELECTOR = 'textarea[aria-hidden="true"][readonly]:not([name])';

/**
 * Names the hidden twin textarea that MUI renders for every multiline input.
 *
 * `TextareaAutosize` renders two textareas: the real one, and an `aria-hidden`,
 * `readOnly`, off-screen copy it measures to size the field. MUI spreads no
 * props into that copy, so there is no way to give it a `name` through the React
 * API — and Chrome's autofill audit reports it as "a form field element should
 * have an id or name attribute".
 *
 * It is a measurement element: it cannot be focused, read by assistive tech, or
 * autofilled, and MUI never reads attributes back off it. Naming it is inert and
 * only serves to keep the audit clean, so this is deliberately a DOM-level
 * workaround for an upstream gap rather than a fix to our own markup. If MUI
 * changes the internals the selector stops matching and the audit entry returns;
 * nothing else breaks.
 */
export const useShadowTextareaNames = () => {
  useEffect(() => {
    const name = (shadow: Element) => {
      // Borrow the real input's identity so the pair is obvious in devtools.
      const real = shadow.parentElement?.querySelector<
        HTMLInputElement | HTMLTextAreaElement
      >("textarea:not([aria-hidden]), input:not([aria-hidden])");
      const base = real?.name || real?.id || "textarea";
      shadow.setAttribute("name", `${base}-autosize-shadow`);
    };

    const sweep = (target: ParentNode) => {
      target.querySelectorAll(SHADOW_SELECTOR).forEach(name);
    };

    sweep(document);

    /*
     * Added subtrees are collected and swept once per frame rather than scanned
     * per mutation record. This observer watches the whole document for the
     * app's lifetime, and views like the scheduler mount hundreds of nodes in a
     * single batch — a querySelectorAll per record turns one render into
     * hundreds of tree walks.
     */
    const pendingRoots = new Set<Element>();
    let frameId: number | null = null;

    const flush = () => {
      frameId = null;
      for (const root of pendingRoots) {
        // A node may have been removed again between batching and this frame.
        if (!root.isConnected) continue;
        if (root.matches(SHADOW_SELECTOR)) name(root);
        else sweep(root);
      }
      pendingRoots.clear();
    };

    const observer = new MutationObserver(records => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node instanceof Element) pendingRoots.add(node);
        }
      }

      if (pendingRoots.size > 0 && frameId === null) {
        frameId = requestAnimationFrame(flush);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (frameId !== null) cancelAnimationFrame(frameId);
      pendingRoots.clear();
    };
  }, []);
};
